import { AutomationForgeElementType } from "@redotech/redo-content-builder-common/forge/marketing-automation-forge-types";
import {
  RedoBadge,
  RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { isDef } from "@redotech/util/checks";
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  MarkerType,
} from "@xyflow/react";
import { DropZone } from "../nodes/drop-zone-node";
import * as EdgeCSS from "./edge.module.css";

export function LabeledEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  data,
}: {
  id: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  data: {
    label: string;
    color: RedoBadgeColor;
    idFrom?: string;
    idTo?: string;
    conditionSide?: boolean;
    edgeId?: string;
    elementDropped?: (
      elementType: AutomationForgeElementType,
      idFrom: string,
      idTo?: string,
      conditionSide?: boolean,
      edgeId?: string,
    ) => void;
  };
}) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    borderRadius: 20,
  });

  return (
    <>
      <BaseEdge
        className={EdgeCSS.line}
        id={id}
        markerEnd={MarkerType.Arrow}
        path={edgePath}
      />
      <EdgeLabelRenderer>
        {data.elementDropped && isDef(data.idFrom) && (
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -100%) translate(${targetX}px, ${targetY - 10 + 32}px)`,
              pointerEvents: "all",
            }}
          >
            {DropZone(
              data.idFrom,
              data.elementDropped,
              data.idTo,
              data.conditionSide,
              false, // isNode parameter
              data.edgeId,
            )}
          </div>
        )}
        <div
          style={{
            position: "absolute",
            transform: (() => {
              // Only apply special positioning for AB test variant edges
              if (data.edgeId) {
                // Only adjust X position - keep Y position the same as original
                // Calculate a percentage based on horizontal distance
                const xDistance = Math.abs(targetX - sourceX);
                // More dramatic scaling:
                // - Closest nodes: almost at midpoint (0.51)
                // - Medium distance: around 65% of the way
                // - Furthest nodes: very close to target (0.85)
                const xPercentage = Math.min(
                  0.85,
                  0.51 + (xDistance * xDistance) / 250_000,
                );
                // Calculate new X position
                const newX = sourceX + (targetX - sourceX) * xPercentage;
                return `translate(-50%, -50%) translate(${newX}px, ${labelY}px)`;
              } else {
                // For all other edges, use the default position
                return `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`;
              }
            })(),
            pointerEvents: "all",
          }}
        >
          <RedoBadge color={data.color} text={data.label} />
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
